import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contentId", "lessonId", "language", "trackId"],
  _excluded2 = ["shortId"],
  _excluded3 = ["shortId"];
import Raven from 'raven-js';
import I18n from 'I18n';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { na1 } from 'hubspot-url-utils/hublets';
import ACVideoPlayer from 'academy-common-lib/components/video/ACVideoPlayer';
import { Image } from 'foundations-components/transitional/components/Image';
import { Metrics } from '../../../Metrics';
import { useVideo } from '../videoQueries';
import { useVideoPlayerConfig } from '../videoHooks';
import { useShortVideo } from '../../shortContent/shortContentHooks';
import UsageTracker from '../../../utils/UsageTracker';
import { usePlaylistIdFromSearch } from '../../playlistContent/hooks';
import { usePlaylist } from '../../savedItems/savedItemsQueries';
import { useIsHubspotPlaylist } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import { HS_VIDEO_SURFACE, trackHsVideoComponentRender, trackHsVideoEngaged, trackHsVideoFailedToRender, trackHsVideoPlayClicked, trackHsVideoRenderedSuccessfully } from 'academy-common-lib/utils/hsVideoMetrics';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { jsx as _jsx } from "react/jsx-runtime";
const EmbeddedVideoBase = ({
  autoPlay,
  onFinish,
  onPlay,
  onProgress,
  onActivation,
  onLoaded,
  onReady,
  onTimeUpdate,
  mutedByDefault,
  playSpeed,
  fallbackThumbnailUrl,
  videoId,
  creatorHubId,
  visibility,
  creatorRegion,
  contentId,
  startTime,
  language,
  subtitleLanguage,
  renderContext = 'embedded-academy',
  videoType,
  lessonId,
  shortId,
  trackId,
  playerType
}) => {
  const playlistId = usePlaylistIdFromSearch();
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlist);
  const playbackRate = playSpeed && playSpeed / 100;
  const {
    setSubtitleLanguage,
    setPlaySpeed,
    toggleAutoPlay
  } = useVideoPlayerConfig();
  const itemVisibility = visibility ? readUnhandledEnumCase(visibility) : undefined;
  const itemVideoType = videoType ? readUnhandledEnumCase(videoType) : undefined;
  const handleToggleAutoPlay = () => toggleAutoPlay();
  const previousSelectedLanguage = useRef(subtitleLanguage);
  // Cache the video player instance for seeking operations
  const currentEmbedPlayer = useRef(null);

  // Find and cache the video player instance by videoId from the global hsVideoApi
  const resolvePlayer = useCallback(() => {
    var _window$hsVideoApi;
    if (currentEmbedPlayer.current) return;
    const hsEmbedPlayers = ((_window$hsVideoApi = window.hsVideoApi) === null || _window$hsVideoApi === void 0 ? void 0 : _window$hsVideoApi.getPlayers()) || [];
    const player = hsEmbedPlayers.find(p => {
      var _p$options, _p$options$toString, _videoId$toString;
      return (p === null || p === void 0 || (_p$options = p.options) === null || _p$options === void 0 || (_p$options = _p$options.id) === null || _p$options === void 0 || (_p$options$toString = _p$options.toString) === null || _p$options$toString === void 0 ? void 0 : _p$options$toString.call(_p$options)) === (videoId === null || videoId === void 0 || (_videoId$toString = videoId.toString) === null || _videoId$toString === void 0 ? void 0 : _videoId$toString.call(videoId));
    }) || null;
    currentEmbedPlayer.current = player;
  }, [videoId]);
  const handleOnProgress = useCallback(({
    currentTime,
    subtitleLanguage: _language
  }) => {
    var _currentEmbedPlayer$c;
    previousSelectedLanguage.current = subtitleLanguage;
    const hasSubtitle = !!((_currentEmbedPlayer$c = currentEmbedPlayer.current) !== null && _currentEmbedPlayer$c !== void 0 && (_currentEmbedPlayer$c = _currentEmbedPlayer$c.video) !== null && _currentEmbedPlayer$c !== void 0 && (_currentEmbedPlayer$c = _currentEmbedPlayer$c.subtitleLanguageCodes) !== null && _currentEmbedPlayer$c !== void 0 && _currentEmbedPlayer$c.length);
    if (onProgress) {
      onProgress({
        currentTime,
        subtitleLanguage: _language
      });
    }
    if (previousSelectedLanguage.current !== _language && hasSubtitle) {
      setSubtitleLanguage(_language);
    }
  }, [onProgress, setSubtitleLanguage, subtitleLanguage]);

  // if the video is played from a playlist, we need to track the playlist info
  const playlistInfo = useMemo(() => {
    return Object.assign({}, playlistId && {
      playlistId
    }, playlist && {
      isHubspotPlaylist
    }, playlist && {
      playlistName: playlist.name
    });
  }, [playlistId, playlist, isHubspotPlaylist]);
  const visibilityProps = useMemo(() => visibility ? {
    visibility
  } : {}, [visibility]);
  const onVideoLoaded = useCallback(() => {
    trackHsVideoRenderedSuccessfully(Metrics, HS_VIDEO_SURFACE.LEARNING_CENTER_EMBEDDED_VIDEO);
    UsageTracker.trackStandalone('Video Usage', Object.assign({
      type: 'loaded',
      playerType: 'HUBSPOT_VIDEO',
      creatorHubId,
      videoId,
      contentId,
      renderContext,
      videoType: itemVideoType,
      lessonId,
      shortId,
      visibility: itemVisibility
    }, playlistInfo));
    // Ensure player ref is set when video finishes loading (fallback to onReady)
    resolvePlayer();
    if (onLoaded) onLoaded();
  }, [creatorHubId, videoId, contentId, playlistInfo, onLoaded, renderContext, lessonId, shortId, resolvePlayer, itemVisibility, itemVideoType]);
  const handlePlay = useCallback(() => {
    UsageTracker.trackStandalone('Video Usage', Object.assign({
      type: 'started',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      autoPlay,
      creatorHubId,
      contentId,
      renderContext,
      videoType: itemVideoType,
      lessonId,
      shortId,
      visibility: itemVisibility
    }, playlistInfo));
    trackHsVideoPlayClicked(Metrics, {
      surface: HS_VIDEO_SURFACE.LEARNING_CENTER_EMBEDDED_VIDEO
    });
    if (onPlay) onPlay();
  }, [videoId, autoPlay, creatorHubId, contentId, playlistInfo, onPlay, renderContext, lessonId, shortId, itemVisibility, itemVideoType]);
  const handleFinish = useCallback(() => {
    UsageTracker.trackStandalone('Video Usage', Object.assign({
      type: 'finished',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      creatorHubId,
      contentId,
      renderContext,
      videoType: itemVideoType,
      lessonId,
      shortId,
      visibility: itemVisibility
    }, playlistInfo));
    if (onFinish) onFinish();
  }, [videoId, creatorHubId, contentId, playlistInfo, onFinish, renderContext, lessonId, shortId, itemVisibility, itemVideoType]);
  const handleActivation = useCallback(() => {
    UsageTracker.trackStandalone('Video Usage', Object.assign({
      type: 'engaged',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      creatorHubId,
      contentId,
      renderContext,
      videoType: itemVideoType,
      lessonId,
      shortId
    }, playlistInfo, {
      visibility: itemVisibility
    }));
    trackHsVideoEngaged(Metrics, {
      surface: HS_VIDEO_SURFACE.LEARNING_CENTER_EMBEDDED_VIDEO
    });
    if (onActivation) onActivation();
  }, [videoId, creatorHubId, contentId, playlistInfo, onActivation, renderContext, lessonId, shortId, itemVisibility, itemVideoType]);
  const handleMinutesWatched = useCallback(viewDuration => {
    UsageTracker.trackStandalone('Video Usage', Object.assign({
      type: 'minutes-watched',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      viewDuration,
      creatorHubId,
      contentId,
      renderContext,
      videoType: itemVideoType,
      lessonId,
      shortId
    }, playlistInfo, {
      visibility: itemVisibility
    }));
  }, [videoId, creatorHubId, contentId, playlistInfo, renderContext, lessonId, shortId, itemVisibility, itemVideoType]);
  const hanldeOnPlayBackRateChange = useCallback(e => {
    const videoSpeed = e.detail.playbackRate;
    setPlaySpeed(videoSpeed * 100);
  }, [setPlaySpeed]);
  const handleReady = () => {
    resolvePlayer();
    if (onReady) onReady();
  };
  const hsVideoOnError = useCallback(e => {
    const {
      errorType,
      payload,
      message
    } = e.detail;
    const resolvedErrorType = errorType || 'UNKNOWN';
    trackHsVideoFailedToRender(Metrics, {
      errorType: resolvedErrorType,
      surface: HS_VIDEO_SURFACE.LEARNING_CENTER_EMBEDDED_VIDEO
    });
    Raven.captureException(new Error(`HS_VIDEO_FAILED_RENDER: ${resolvedErrorType}`), {
      tags: {
        hsVideoErrorType: resolvedErrorType
      },
      fingerprint: ['hs-video-failed-render', resolvedErrorType],
      extra: {
        videoId,
        creatorHubId,
        errorMessage: payload || message
      }
    });
  }, [creatorHubId, videoId]);
  useEffect(() => {
    if (!videoId) return;
    trackHsVideoComponentRender(Metrics, HS_VIDEO_SURFACE.LEARNING_CENTER_EMBEDDED_VIDEO);
  }, [videoId]);
  useEffect(() => {
    if (videoId) {
      UsageTracker.trackStandalone('Video Usage', Object.assign({
        type: 'rendered',
        playerType: 'HUBSPOT_VIDEO',
        creatorHubId
      }, visibilityProps, {
        videoId,
        contentId,
        renderContext,
        videoType: itemVideoType,
        lessonId,
        shortId,
        visibility: itemVisibility
      }, playlistInfo));
    }
  }, [contentId, creatorHubId, visibilityProps, videoId, renderContext, playlistInfo, videoType, lessonId, shortId, itemVisibility, itemVideoType]);
  const captionLanguage = mutedByDefault && language ? subtitleLanguage || language.toLowerCase() : subtitleLanguage;
  const effectiveStartTime = useMemo(() => {
    // Honor explicit startTime (including 0), but ignore invalid negative numbers
    if (typeof startTime === 'number' && startTime >= 0) return startTime;

    // Use ternary operators to determine id and type more safely
    const {
      id,
      type
    } = trackId ? {
      id: Number(trackId) || 0,
      type: 'TRACK'
    } : shortId ? {
      id: shortId,
      type: 'SHORT_FORM_CONTENT'
    } : lessonId ? {
      id: Number(lessonId) || 0,
      type: 'LESSON'
    } : {
      id: Number(contentId) || 0,
      type: 'TRACK'
    };
    if (!id || !language) {
      return 0;
    }
    const sessionKey = JSON.stringify({
      id,
      type,
      language: language.toUpperCase()
    });
    const savedTime = sessionStorage.getItem(sessionKey);
    const parsed = Number(savedTime);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [startTime, lessonId, shortId, trackId, contentId, language]);
  return videoId ? /*#__PURE__*/_jsx("div", {
    "data-test-id": "video-player-container",
    children: /*#__PURE__*/_jsx(ACVideoPlayer, {
      autoPlay: autoPlay,
      creatorHubId: creatorHubId,
      creatorRegion: creatorRegion || na1,
      enableAutoPlayToggle: true,
      mutedByDefault: mutedByDefault,
      onActivation: handleActivation,
      onError: hsVideoOnError,
      onFinish: handleFinish,
      onLoaded: onVideoLoaded,
      onPlay: handlePlay,
      onPlayBackRateChange: hanldeOnPlayBackRateChange,
      onProgress: handleOnProgress,
      onReady: handleReady,
      onTimeProgress: handleMinutesWatched,
      onTimeUpdate: onTimeUpdate,
      onToggleAutoPlay: handleToggleAutoPlay,
      playbackRate: playbackRate,
      playerType: playerType,
      startTime: effectiveStartTime,
      subtitleLanguage: captionLanguage,
      videoId: videoId
    }, contentId)
  }) : /*#__PURE__*/_jsx(Image, {
    alt: I18n.text('learningCenter.overviewModal.thumbnail'),
    "data-test-id": "fallback-thumbnail",
    src: fallbackThumbnailUrl || ''
  });
};
const EmbeddedVideoTrackLesson = _ref => {
  let {
      contentId,
      lessonId,
      language,
      trackId
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const videoProps = useVideo({
    contentId,
    lessonId,
    trackId
  });
  if (!videoProps) return null;
  return /*#__PURE__*/_jsx(EmbeddedVideoBase, Object.assign({
    contentId: contentId,
    lessonId: lessonId,
    trackId: trackId
  }, videoProps, props, {
    language: language
  }));
};
const EmbeddedVideoShort = _ref2 => {
  let {
      shortId
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const videoProps = Object.assign({}, useShortVideo({
    shortId: shortId
  }));
  if (!videoProps) return null;
  return /*#__PURE__*/_jsx(EmbeddedVideoBase, Object.assign({
    contentId: shortId.toString(),
    shortId: shortId
  }, videoProps, props));
};
export const EmbeddedVideo = _ref3 => {
  let {
      shortId
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  if (shortId) return /*#__PURE__*/_jsx(EmbeddedVideoShort, Object.assign({
    shortId: shortId
  }, props));else return /*#__PURE__*/_jsx(EmbeddedVideoTrackLesson, Object.assign({}, props));
};
export default EmbeddedVideo;